import type { ReactNode } from 'react';

import { InputAffixLabel, InputContainer, InputElement } from './Input.styled';
import type {
  InputAffixVariant,
  InputStatus,
  InputStyleVariant,
} from './Input.types';
import type { InputProps } from './InputProps';

type Props = {
  inputProps: InputProps;
  prefix?: ReactNode;
  suffix?: ReactNode;
  hideSuffixWhenEmpty?: boolean;
  status?: InputStatus;
  affixVariant?: InputAffixVariant;
  styleVariant?: InputStyleVariant;
  fullHeight?: boolean;
  fullWidth?: boolean;
};

export function Input({
  inputProps,
  prefix,
  suffix,
  hideSuffixWhenEmpty,
  status = 'neutral',
  affixVariant = 'default',
  styleVariant = 'default',
  fullHeight,
  fullWidth,
}: Props) {
  return (
    <InputContainer
      $status={status}
      $affixVariant={affixVariant}
      $styleVariant={styleVariant}
      $fullHeight={Boolean(fullHeight)}
      $fullWidth={Boolean(fullWidth)}
      $hideSuffixWhenEmpty={Boolean(suffix && hideSuffixWhenEmpty)}
      $disabled={inputProps.disabled}
      $readOnly={inputProps.readOnly}
    >
      {prefix && (
        <InputAffixLabel htmlFor={inputProps.id}>{prefix}</InputAffixLabel>
      )}
      <InputElement
        aria-activedescendant={inputProps['aria-activedescendant']}
        aria-autocomplete={inputProps['aria-autocomplete']}
        aria-controls={inputProps['aria-controls']}
        aria-expanded={inputProps['aria-expanded']}
        aria-haspopup={inputProps['aria-haspopup']}
        aria-invalid={inputProps['aria-invalid']}
        aria-label={inputProps['aria-label']}
        autoComplete={inputProps.autoComplete}
        // oxlint-disable-next-line jsx_a11y/no-autofocus
        autoFocus={inputProps.autoFocus}
        data-modal-focus={inputProps.dataModalFocus}
        data-qa-id={inputProps.qaId}
        required={inputProps.required}
        disabled={inputProps.disabled}
        id={inputProps.id}
        max={inputProps.max}
        min={inputProps.min}
        maxLength={inputProps.maxLength}
        name={inputProps.name}
        onBlur={inputProps.onBlur}
        onChange={inputProps.onChange}
        onClick={inputProps.onClick}
        onFocus={inputProps.onFocus}
        onKeyDown={inputProps.onKeyDown}
        onKeyPress={inputProps.onKeyPress}
        onKeyUp={inputProps.onKeyUp}
        placeholder={inputProps.placeholder}
        readOnly={inputProps.readOnly}
        role={inputProps.role}
        ref={inputProps.ref}
        step={inputProps.step}
        title={inputProps.title}
        type={inputProps.type}
        inputMode={inputProps.inputMode}
        value={inputProps.value}
        defaultValue={inputProps.defaultValue}
      />
      {suffix && (
        <InputAffixLabel htmlFor={inputProps.id}>{suffix}</InputAffixLabel>
      )}
    </InputContainer>
  );
}
